import { applyToPoint, type Matrix, type Point } from '../matrix';

/**
 * Normalized absolute path segments. Every supported SVG command is converted to
 * this small vocabulary: move, line, cubic Bézier, and close. Quadratic curves are
 * elevated exactly; elliptical arcs are approximated by cubic segments (see arc.ts).
 */
export type Segment =
  | { readonly type: 'M'; readonly x: number; readonly y: number }
  | { readonly type: 'L'; readonly x: number; readonly y: number }
  | {
      readonly type: 'C';
      readonly x1: number;
      readonly y1: number;
      readonly x2: number;
      readonly y2: number;
      readonly x: number;
      readonly y: number;
    }
  | { readonly type: 'Z' };

export function transformSegments(segments: readonly Segment[], m: Matrix): Segment[] {
  return segments.map((s): Segment => {
    switch (s.type) {
      case 'M':
      case 'L': {
        const p = applyToPoint(m, s);
        return { type: s.type, x: p.x, y: p.y };
      }
      case 'C': {
        const p1 = applyToPoint(m, { x: s.x1, y: s.y1 });
        const p2 = applyToPoint(m, { x: s.x2, y: s.y2 });
        const p = applyToPoint(m, s);
        return { type: 'C', x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y, x: p.x, y: p.y };
      }
      case 'Z':
        return s;
    }
  });
}

/** Formats a number for SVG output with bounded precision; never emits `-0` or exponents. */
export function formatNumber(value: number, precision = 4): string {
  if (!Number.isFinite(value)) throw new RangeError(`Cannot serialize non-finite number ${value}`);
  let text = value.toFixed(precision);
  if (text.includes('.')) text = text.replace(/0+$/, '').replace(/\.$/, '');
  return text === '-0' ? '0' : text;
}

export function serializeSegments(segments: readonly Segment[], precision = 4): string {
  const f = (n: number) => formatNumber(n, precision);
  const parts: string[] = [];
  for (const s of segments) {
    switch (s.type) {
      case 'M':
      case 'L':
        parts.push(`${s.type} ${f(s.x)} ${f(s.y)}`);
        break;
      case 'C':
        parts.push(`C ${f(s.x1)} ${f(s.y1)} ${f(s.x2)} ${f(s.y2)} ${f(s.x)} ${f(s.y)}`);
        break;
      case 'Z':
        parts.push('Z');
        break;
    }
  }
  return parts.join(' ');
}

/** Iterates drawn pieces with their start points; skips moves that draw nothing. */
export function* drawnPieces(
  segments: readonly Segment[],
): Generator<{ readonly start: Point; readonly segment: Segment; readonly subpathStart: Point }> {
  let current: Point = { x: 0, y: 0 };
  let subpathStart: Point = current;
  for (const s of segments) {
    switch (s.type) {
      case 'M':
        current = { x: s.x, y: s.y };
        subpathStart = current;
        break;
      case 'L':
      case 'C':
        yield { start: current, segment: s, subpathStart };
        current = { x: s.x, y: s.y };
        break;
      case 'Z':
        yield { start: current, segment: s, subpathStart };
        current = subpathStart;
        break;
    }
  }
}

/** Counts segments that produce visible geometry (used for complexity limits and empty checks). */
export function countDrawnSegments(segments: readonly Segment[]): number {
  let count = 0;
  for (const piece of drawnPieces(segments)) {
    if (piece.segment.type !== 'Z') count += 1;
  }
  return count;
}
